import { useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import { EmptyField } from 'components/elements/EmptyField';
import Row from 'components/elements/InfoModal/Row';
import { Label } from 'components/elements/Label';
import { Text } from 'components/elements/Text';
import { ModalHeader } from 'components/layout/ModalHeader';
import { ModalStatus } from 'components/layout/ModalStatus';
import MasterDocumentModal from 'components/modals/documentModals/MasterDocumentModal';
import DocumentRelatedCompanies from 'components/widgets/DocumentModals/DocumentRelatedCompanies';
import { InfoModal } from 'components/widgets/InfoModal';
import { useDevelopmentPermit } from 'hooks/useDevelopmentPermit';
import { useDevelopmentPermitV4 } from 'hooks/useDocuments';
import { useGetModalActions, useGetQueryParam } from 'hooks/useModal';
import { trackProjectMaterials } from 'utils/analytics';
import { City } from 'utils/constants/location_legacy';
import { parseRelatedContactsIntoList } from 'utils/contacts';
import { formatProjectCost } from 'utils/currency';

export const DevelopmentPermit = ({
  enableFetchByDocumentId = false,
}: {
  enableFetchByDocumentId?: boolean;
}) => {
  let resolvedDocumentId: string | undefined;
  let result: any;

  if (enableFetchByDocumentId) {
    const documentId = useGetQueryParam('documentId');
    resolvedDocumentId = documentId || undefined;
    result = useDevelopmentPermitV4(documentId || undefined);
  } else {
    const documentUuid = useGetQueryParam('developmentPermit');
    resolvedDocumentId = documentUuid || undefined;
    result = useDevelopmentPermit(documentUuid);
  }
  const { Skeleton, ...modalProps } = useGetModalActions(result.isLoading);

  const permitUrl = result.data?.document_url;
  const isCityToronto = result.data?.city?.toUpperCase() === City.TORONTO;
  const relatedContacts = useMemo(
    () => parseRelatedContactsIntoList(result.data?.metadata?.relatedcontacts || []),
    [result.data?.metadata],
  );

  return (
    <InfoModal
      {...modalProps}
      header={
        <ModalHeader Skeleton={Skeleton} type="Site Plan">
          {result.data?.metadata?.permitnum || 'N/A'}
        </ModalHeader>
      }
    >
      <Box>
        <MasterDocumentModal
          address={result.data?.metadata?.parcel_address ?? ''}
          city={result.data?.city ?? ''}
          state_code={result.data?.state_code ?? ''}
          isLoading={result.isLoading}
        />
        {/* Companies */}
        <DocumentRelatedCompanies
          contacts={relatedContacts}
          companies={result.data?.companies}
          documentId={resolvedDocumentId}
          prefetchedAiContactSuggestionsByRelatedContactId={
            result.data?.ai_related_contact_suggestions_by_contact_id
          }
          Skeleton={Skeleton}
        />

        {/* Project Name */}
        <InfoModal.Row>
          <Label>Project Name</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {result.data?.metadata?.projectname ? (
                <Text fontWeight="bold">{result.data.metadata.projectname}</Text>
              ) : (
                <EmptyField />
              )}
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>

        {/* Description */}
        <InfoModal.Row hideDivider>
          <Label>Description</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {result.data?.metadata?.description ? (
                <Text>{result.data.metadata.description}</Text>
              ) : (
                <EmptyField />
              )}
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>

        {/* Project Value */}
        <InfoModal.Row hideDivider>
          <Label>Project Value</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {result.data?.metadata?.estprojectcost ? (
                <Text>{formatProjectCost(result.data?.metadata?.estprojectcost)}</Text>
              ) : (
                <EmptyField />
              )}
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>

        {/* Category */}
        <InfoModal.Row hideDivider>
          <Label>Category</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {result.data?.metadata?.category ? (
                <Text>{result.data.metadata.category}</Text>
              ) : (
                <EmptyField />
              )}
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>

        {/* Proposed Use */}
        <InfoModal.Row hideDivider>
          <Label>Proposed Use</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {result.data?.metadata?.proposedusedescription ? (
                <Text>{result.data.metadata.proposedusedescription}</Text>
              ) : (
                <EmptyField />
              )}
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>

        {/* Status */}
        <ModalStatus Skeleton={Skeleton} activities={result.data?.activities} />

        {/* Relevant Materials */}
        {result.isLoading && (
          <InfoModal.Row>
            <Label>Relevant Materials</Label>
            <InfoModal.RowDetails>
              <Skeleton variant="text" noOfLines={1} />
            </InfoModal.RowDetails>
          </InfoModal.Row>
        )}
        {!result.isLoading && permitUrl && !isCityToronto && (
          <Row
            label="Relevant Materials"
            document={result.data}
            clickHandler={() => {
              trackProjectMaterials(permitUrl);
            }}
          />
        )}
      </Box>
    </InfoModal>
  );
};
